// The lead registry's "fields for classification" API, on the mock: made-up but fixed fields per lead (from the seed
// and the lead id), worked out when asked and never stored. The run reads them, keeps only the result as tags, and a
// trace names the rule, never the value (plan v2 §4.1).
import type { ClassifyField } from '../../schemas'
import { unitFor } from '../random'
import type { LeadRecord, State } from '../state'

/** 3-digit PIN prefix → city, state. The first 8 are the metros (the seed's metro PIN list). */
export const PIN_PLACES: [prefix: string, city: string, state: string][] = [
  ['110', 'Delhi', 'Delhi'],
  ['400', 'Mumbai', 'Maharashtra'],
  ['560', 'Bengaluru', 'Karnataka'],
  ['600', 'Chennai', 'Tamil Nadu'],
  ['700', 'Kolkata', 'West Bengal'],
  ['500', 'Hyderabad', 'Telangana'],
  ['411', 'Pune', 'Maharashtra'],
  ['380', 'Ahmedabad', 'Gujarat'],
  ['302', 'Jaipur', 'Rajasthan'],
  ['226', 'Lucknow', 'Uttar Pradesh'],
  ['452', 'Indore', 'Madhya Pradesh'],
  ['440', 'Nagpur', 'Maharashtra'],
]
const METROS = 8

export interface LeadFields {
  pin: string
  city: string
  state: string
  employment: string
  loan_amount: string
  age_band: string
  income_band: string
}

const pick = <T>(items: readonly T[], unit: number): T => items[Math.floor(unit * items.length)]!

export function fieldsOf(state: State, lead: LeadRecord): LeadFields {
  const u = (what: string) => unitFor(state, `${lead.id}:field:${what}`)
  // a little over half live in a metro
  const place =
    u('metro') < 0.55
      ? pick(PIN_PLACES.slice(0, METROS), u('place'))
      : pick(PIN_PLACES.slice(METROS), u('place'))
  return {
    pin: place[0] + String(Math.floor(u('pin') * 1000)).padStart(3, '0'),
    city: place[1],
    state: place[2],
    employment: u('emp') < 0.6 ? 'salaried' : 'self-employed',
    loan_amount: pick(['under-1l', '1l-5l', 'over-5l'], u('amount')),
    age_band: pick(['21-25', '26-35', '36-45', '46-60'], u('age')),
    income_band: pick(['under-25k', '25k-50k', '50k-1l', 'over-1l'], u('income')),
  }
}

/** The value a field check compares; source and partner come from the lead itself, not the registry. */
export function fieldValue(
  fields: LeadFields,
  lead: LeadRecord,
  field: Exclude<ClassifyField, 'tag'>
): string {
  if (field === 'source') return lead.source
  if (field === 'partner') return lead.partner
  return fields[field]
}
